import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import config from "../../../config";

export const Profilepage = () => {
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth || {});
  const { customer_data, phone } = auth;

  // State for editable form data
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    gender: "",
    
    address: {
      house: "",
      locality: "",
      city: "",
      state: "",
      country: "India",
      pincode: "",
    },
    extra_phone: "",
    language: "English",
  });

  // State for profile photo upload
  const [profilePhoto, setProfilePhoto] = useState("/default-profile.png");
  const [file, setFile] = useState(null);

  // Populate formData and profilePhoto when auth loads
  useEffect(() => {
    if (auth) {
      setFormData({
        name: auth.name || "",
        email: auth.email || "",
        gender: auth.gender || "",
       
        address: {
          house: auth.address?.house || "",
          locality: auth.address?.locality || "",
          city: auth.address?.city || "",
          state: auth.address?.state || "",
          country: auth.address?.country || "India",
          pincode: auth.address?.pincode || "",
        },
        extra_phone: auth.extra_phone || "",
        language: auth.language || "English",
      });
      setProfilePhoto(customer_data?.profile_photo || "/default-profile.png");
    }
  }, [auth, customer_data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const key = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        address: { ...prev.address, [key]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle file selection for profile photo
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => setProfilePhoto(reader.result);
      reader.readAsDataURL(selectedFile);
    }
  };
  const handleSaveChanges = async () => {
    try {
      const token = localStorage.getItem("token");

      // Prepare the full update object
      const updatedData = {
        name: formData.name,
        contact: phone, // always required
        email: formData.email,
        gender: formData.gender,
        dob: formData.dob,
        blood_group: formData.blood_group,
        timezone: formData.timezone,
        language: formData.language,
        extraContact: formData.extra_phone,
        address: formData.address, // send the full nested object
        profile_photo: profilePhoto, // if the image is a base64 URL
      };

      const res = await fetch(
        `${config.API_BASE_URL}/customer/updatecustomer`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updatedData),
        }
      );

      const data = await res.json();

      if (data.success) {
        toast("Profile updated successfully ✅");
        console.log(data);
      } else {
        toast(data.message || "Failed to update profile ❌");
      }
    } catch (error) {
      console.error("Update failed:", error);
      toast("Something went wrong while updating profile ❌");
    }
  };

  const handleGoBack = () => navigate(-1);

  return (
    <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
      <div className="max-w-6xl w-full mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={handleGoBack}
            className="flex items-center text-[#2A2A72] hover:text-[#2A2A72] transition"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-semibold">Back</span>
          </button>
          <h1 className="text-4xl font-extrabold text-[#2A2A72] hidden md:block">
            Account Settings ⚙️
          </h1>
          <div></div>
        </div>

        {/* Profile Photo & Name */}
        <div className="bg-white shadow-xl rounded-2xl p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="w-28 h-28 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-4 border-white shadow-md">
              <img
                src={profilePhoto}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-center sm:text-left">
              <p className="text-gray-500 text-sm">
                Pick a photo from your computer
              </p>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="mt-2"
              />
            </div>
          </div>

          <div className="flex-1 w-full md:w-auto">
            <label className="block text-[#2A2A72] font-bold mb-2">
              Full Name*
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-4 focus:ring-orange-300 transition"
            />
          </div>
        </div>

        {/* Contact & Personal Info */}
        <div className="bg-white shadow-xl rounded-2xl p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div>
            <label className="block text-[#2A2A72] font-bold mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={phone || ""}
              disabled
              className="w-full border border-gray-300 rounded-lg px-4 py-3 bg-gray-100 cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-[#2A2A72] font-bold mb-2">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-4 focus:ring-orange-300 transition"
            />
          </div>

          <div>
            <label className="block text-[#2A2A72] font-bold mb-2">
              Gender
            </label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 bg-white focus:outline-none focus:ring-4 focus:ring-orange-300 transition"
            >
              <option value="">Select an option</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-[#2A2A72] font-bold mb-2">
              Date of Birth
            </label>
            <input
              type="date"
              name="dob"
              value={formData.dob}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 bg-white focus:outline-none focus:ring-4 focus:ring-orange-300 transition"
            />
          </div>

         

         
        </div>

        {/* Address */}
        <div className="bg-white shadow-xl rounded-2xl p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {["house", "locality", "city", "state", "country", "pincode"].map(
            (field) => (
              <div key={field}>
                <label className="block text-[#2A2A72] font-bold mb-2">
                  {field.charAt(0).toUpperCase() + field.slice(1)}
                </label>
                {field === "country" ? (
                  <select
                    name={`address.${field}`}
                    value={formData.address[field]}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 bg-white focus:outline-none focus:ring-4 focus:ring-orange-300 transition"
                  >
                    <option value="India">India</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    name={`address.${field}`}
                    value={formData.address[field]}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-4 focus:ring-orange-300 transition"
                  />
                )}
              </div>
            )
          )}
        </div>

        {/* Other Info */}
        <div className="bg-white shadow-xl rounded-2xl p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <label className="block text-[#2A2A72] font-bold mb-2">
              Extra Phone Numbers
            </label>
            <input
              type="tel"
              name="extra_phone"
              value={formData.extra_phone}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-4 focus:ring-orange-300 transition"
            />
          </div>
          <div>
            <label className="block text-[#2A2A72] font-bold mb-2">
              Language
            </label>
            <select
              name="language"
              value={formData.language}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 bg-white focus:outline-none focus:ring-4 focus:ring-orange-300 transition"
            >
              <option value="English">English</option>
            </select>
          </div>
        </div>

        {/* Save */}
        <div className="flex justify-end mt-8">
          <button
            onClick={handleSaveChanges}
            className="bg-[#FF6B00] text-white font-bold px-8 py-3 rounded-lg shadow-md hover:bg-[#E56300] transition transform hover:scale-105"
          >
            Save Changes ✨
          </button>
        </div>
      </div>
    </div>
  );
};
